import { collection, addDoc, serverTimestamp, query, where, getDocs, doc, updateDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Service pour gérer l'envoi d'emails via Resend et Supabase Edge Functions
 */
export const emailService = {
  /**
   * Envoie un email via Supabase Edge Function
   */
  sendEmailViaEdgeFunction: async (to: string | string[], subject: string, text: string, html: string, from?: string): Promise<void> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Configuration Supabase manquante');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          to,
          subject,
          text,
          html,
          from,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Erreur lors de l\'envoi de l\'e-mail:', error);
        throw new Error(error.error || 'Erreur lors de l\'envoi de l\'e-mail');
      }

      const result = await response.json();
      console.log('E-mail envoyé avec succès:', result);
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'e-mail:', error);
      throw error;
    }
  },
  /**
   * Planifie l'envoi d'un email de rappel de rendez-vous
   * @param appointment Les données du rendez-vous
   * @param alertDate La date d'envoi de l'alerte
   * @param alertId L'identifiant de l'alerte à mettre à jour (optionnel)
   * @returns Promise<void>
   */
  scheduleAppointmentReminder: async (appointment: any, alertDate: Date, alertId?: string): Promise<void> => {
    if (!auth.currentUser || !auth.currentUser.email) {
      throw new Error('Utilisateur non connecté ou email manquant');
    }

    const appointmentDate = new Date(appointment.date);

    const htmlContent = `
      <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
        <h2 style="color: #2563eb;">Rappel de rendez-vous</h2>
        <p>Bonjour,</p>
        <p>Ceci est un rappel pour votre rendez-vous :</p>
        <ul style="list-style: none; padding: 0;">
          <li style="padding: 8px 0;"><strong>Entreprise:</strong> ${appointment.title}</li>
          <li style="padding: 8px 0;"><strong>Contact:</strong> ${appointment.contact}</li>
          <li style="padding: 8px 0;"><strong>Date:</strong> ${format(appointmentDate, 'dd/MM/yyyy', { locale: fr })}</li>
          <li style="padding: 8px 0;"><strong>Heure:</strong> ${appointment.time}</li>
          <li style="padding: 8px 0;"><strong>Agence:</strong> ${appointment.agency}</li>
        </ul>
        <p>Cordialement,<br><strong>L'équipe Anthea RH</strong></p>
      </div>
    `;

    const textContent = `Rappel: Vous avez un rendez-vous avec ${appointment.contact} de ${appointment.title} le ${format(appointmentDate, 'dd/MM/yyyy', { locale: fr })} à ${appointment.time}.`;

    // Si la date d'alerte est maintenant ou passée, envoyer immédiatement
    const now = new Date();
    if (alertDate <= now) {
      try {
        await emailService.sendEmailViaEdgeFunction(
          auth.currentUser.email,
          `Rappel: Rendez-vous avec ${appointment.title}`,
          textContent,
          htmlContent
        );

        // Mettre à jour l'alerte pour marquer l'email comme envoyé
        if (alertId) {
          await updateDoc(doc(db, 'alerts', alertId), {
            emailSent: true,
            emailSentAt: serverTimestamp(),
            emailError: null
          });
        }
      } catch (error) {
        // En cas d'erreur, mettre à jour l'alerte avec l'erreur
        if (alertId) {
          await updateDoc(doc(db, 'alerts', alertId), {
            emailSent: false,
            emailError: error instanceof Error ? error.message : 'Erreur inconnue'
          });
        }
        throw error;
      }
    } else {
      // Sinon, utiliser l'edge function pour ajouter l'email à la queue
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Configuration Supabase manquante');
      }

      const emailData = {
        to: [auth.currentUser.email],
        subject: `Rappel: Rendez-vous avec ${appointment.title}`,
        text: textContent,
        html: htmlContent,
        scheduledFor: alertDate.toISOString(),
        appointmentId: appointment.id,
        alertId: alertId || null,
        userId: auth.currentUser.uid,
        from: 'CRM Anthéa <noreply@crm-emploi.com>'
      };

      console.log('Planification email pour:', alertDate.toISOString());
      console.log('Rendez-vous:', appointment.title, 'à', appointment.time);

      const response = await fetch(`${supabaseUrl}/functions/v1/schedule-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'apikey': supabaseAnonKey
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const error = await response.text();
        console.error('Erreur lors de l\'ajout dans la queue:', error);
        throw new Error(`Erreur lors de l'ajout dans la queue: ${error}`);
      }

      const result = await response.json();
      console.log('Email planifié avec succès dans la queue Supabase pour:', alertDate);
      console.log('Résultat:', result);
    }
  },

  /**
   * Envoie un email immédiatement
   * @param to Adresse email du destinataire
   * @param subject Sujet de l'email
   * @param text Contenu texte de l'email
   * @param html Contenu HTML de l'email
   * @returns Promise<void>
   */
  sendEmail: async (to: string, subject: string, text: string, html: string): Promise<void> => {
    await emailService.sendEmailViaEdgeFunction(to, subject, text, html);
  },

  /**
   * Traite les emails en attente dans la queue via l'Edge Function
   * @returns Promise<{sent: number, failed: number, processed: number}>
   */
  processEmailQueue: async (): Promise<{sent: number, failed: number, processed: number}> => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Configuration Supabase manquante');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/process-email-queue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Erreur lors du traitement de la queue:', error);
        throw new Error(error.error || 'Erreur lors du traitement de la queue');
      }

      const result = await response.json();
      console.log('Queue traitée:', result);

      // Synchroniser les alertes Firebase avec l'état de la queue Supabase
      if (result.processed > 0) {
        await emailService.syncFirebaseAlerts();
      }

      return {
        sent: result.sent || 0,
        failed: result.failed || 0,
        processed: result.processed || 0
      };
    } catch (error) {
      console.error('Error processing email queue:', error);
      throw error;
    }
  },

  /**
   * Synchronise les alertes Firebase avec l'état de la queue Supabase
   */
  syncFirebaseAlerts: async (): Promise<void> => {
    if (!auth.currentUser) return;

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        return;
      }

      // Utiliser l'Edge Function pour récupérer les statuts d'emails
      const response = await fetch(
        `${supabaseUrl}/functions/v1/get-email-status`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId: auth.currentUser.uid })
        }
      );

      if (!response.ok) {
        console.error('Erreur lors de la récupération des statuts d\'emails:', await response.text());
        return;
      }

      const { emails } = await response.json();

      // Mettre à jour les alertes Firebase en fonction du statut des emails
      for (const email of emails) {
        if (email.alertId) {
          const alertRef = doc(db, 'alerts', email.alertId);
          const alertDoc = await getDoc(alertRef);

          if (alertDoc.exists()) {
            if (email.status === 'sent') {
              await updateDoc(alertRef, {
                emailSent: true,
                emailSentAt: email.sentAt ? new Date(email.sentAt) : serverTimestamp(),
                emailError: null
              });
            } else if (email.status === 'failed') {
              await updateDoc(alertRef, {
                emailSent: false,
                emailError: email.error || 'Erreur inconnue'
              });
            }
          }
        }
      }

      console.log('Alertes Firebase synchronisées');
    } catch (error) {
      console.error('Erreur lors de la synchronisation:', error);
    }
  },

  /**
   * Bascule automatiquement les alertes en retard
   * @returns Promise<{updated: number}>
   */
  autoUpdateLateAlerts: async (): Promise<{updated: number}> => {
    if (!auth.currentUser) {
      return { updated: 0 };
    }

    try {
      // Récupérer toutes les alertes "pending" de l'utilisateur
      const alertsQuery = query(
        collection(db, 'alerts'),
        where('userId', '==', auth.currentUser.uid),
        where('status', '==', 'pending')
      );

      const querySnapshot = await getDocs(alertsQuery);

      if (querySnapshot.empty) {
        return { updated: 0 };
      }

      let updated = 0;
      const now = new Date();

      // Pour chaque alerte, vérifier si la date est passée
      for (const alertDoc of querySnapshot.docs) {
        const alert = alertDoc.data();
        const alertDate = alert.date.toDate();

        // Si la date/heure est passée, basculer en "late"
        if (alertDate < now) {
          await updateDoc(doc(db, 'alerts', alertDoc.id), {
            status: 'late',
            updatedAt: serverTimestamp()
          });
          updated++;
        }
      }

      if (updated > 0) {
        console.log(`${updated} alerte(s) basculée(s) en retard automatiquement`);
      }

      return { updated };
    } catch (error) {
      console.error('Erreur lors de la mise à jour automatique des alertes:', error);
      return { updated: 0 };
    }
  },

  /**
   * Resynchronise les alertes en attente et crée les emails manquants dans la queue
   * @returns Promise<{created: number, errors: number}>
   */
  resyncPendingAlerts: async (): Promise<{created: number, errors: number}> => {
    if (!auth.currentUser) {
      throw new Error('Utilisateur non connecté');
    }

    try {
      // Récupérer toutes les alertes en attente (pending) qui n'ont pas d'email envoyé
      const alertsQuery = query(
        collection(db, 'alerts'),
        where('userId', '==', auth.currentUser.uid),
        where('status', '==', 'pending'),
        where('emailSent', '==', false)
      );

      const querySnapshot = await getDocs(alertsQuery);

      if (querySnapshot.empty) {
        console.log('Aucune alerte en attente à resynchroniser');
        return { created: 0, errors: 0 };
      }

      let created = 0;
      let errors = 0;

      // Pour chaque alerte, récupérer le rendez-vous et créer l'email dans la queue
      for (const alertDoc of querySnapshot.docs) {
        const alert = alertDoc.data();

        try {
          // Récupérer le rendez-vous associé
          if (!alert.appointmentId) {
            console.warn('Alerte sans appointmentId:', alertDoc.id);
            errors++;
            continue;
          }

          const appointmentDoc = await getDoc(doc(db, 'appointments', alert.appointmentId));

          if (!appointmentDoc.exists()) {
            console.warn('Rendez-vous introuvable pour l\'alerte:', alertDoc.id);
            errors++;
            continue;
          }

          const appointment = { id: appointmentDoc.id, ...appointmentDoc.data() };

          // Calculer la date d'alerte
          const appointmentDate = new Date(appointment.date);
          const alertMinutes = appointment.alertMinutes || 15;
          const alertDate = new Date(appointmentDate.getTime() - alertMinutes * 60000);

          // Vérifier si la date d'alerte est dans le futur
          const now = new Date();
          if (alertDate <= now) {
            console.log('Alerte déjà passée, pas de création d\'email:', alertDoc.id);
            continue;
          }

          // Créer l'email dans la queue
          await emailService.scheduleAppointmentReminder(
            appointment,
            alertDate,
            alertDoc.id
          );

          created++;
          console.log('Email créé pour l\'alerte:', alertDoc.id);
        } catch (error) {
          console.error('Erreur lors de la création de l\'email pour l\'alerte:', alertDoc.id, error);
          errors++;
        }
      }

      return { created, errors };
    } catch (error) {
      console.error('Erreur lors de la resynchronisation:', error);
      throw error;
    }
  },

  /**
   * Envoie des notifications par email pour les rendez-vous en retard
   * @param email Adresse email du destinataire (par défaut l'utilisateur connecté)
   * @returns Promise<void>
   */
  sendLateAppointmentsNotification: async (email?: string): Promise<void> => {
    if (!auth.currentUser) {
      throw new Error('Utilisateur non connecté');
    }

    // Utiliser l'email de l'utilisateur connecté si aucun email n'est fourni
    const userEmail = email || auth.currentUser.email;
    if (!userEmail) {
      throw new Error('Email manquant');
    }

    try {
      // Récupérer les alertes en retard
      const alertsQuery = query(
        collection(db, 'alerts'),
        where('userId', '==', auth.currentUser.uid),
        where('status', '==', 'late')
      );

      const querySnapshot = await getDocs(alertsQuery);

      if (querySnapshot.empty) {
        console.log('Aucune alerte en retard');
        return;
      }

      // Préparer le contenu de l'email
      const lateAlerts = querySnapshot.docs.map(doc => doc.data());

      let htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #dc2626;">Alertes de rendez-vous en retard</h2>
          <p>Bonjour,</p>
          <p>Voici la liste des rendez-vous en retard :</p>
          <ul style="list-style: none; padding: 0;">
      `;

      let textContent = `Alertes de rendez-vous en retard\n\n`;

      for (const alert of lateAlerts) {
        const alertDate = alert.date.toDate();

        htmlContent += `
          <li style="padding: 12px; margin: 8px 0; border-left: 4px solid #dc2626; background-color: #fef2f2;">
            <strong style="color: #dc2626;">${alert.company}</strong> -
            ${format(alertDate, 'dd/MM/yyyy', { locale: fr })}
            <p style="margin: 4px 0 0 0;">${alert.description}</p>
          </li>
        `;

        textContent += `- ${alert.company} - ${format(alertDate, 'dd/MM/yyyy', { locale: fr })}\n  ${alert.description}\n\n`;
      }

      htmlContent += `
          </ul>
          <p>Cordialement,<br><strong>L'équipe Anthea RH</strong></p>
        </div>
      `;

      textContent += `Cordialement,\nL'équipe Anthea RH`;

      // Envoyer l'email immédiatement
      await emailService.sendEmailViaEdgeFunction(
        userEmail,
        `${lateAlerts.length} rendez-vous en retard - Anthea RH`,
        textContent,
        htmlContent
      );

      console.log(`Email de notification envoyé à ${userEmail}`);
    } catch (error) {
      console.error('Error sending late appointments notification:', error);
      throw error;
    }
  }
};